/**
 * 三康看板 · /sk（底部导航第二项「三康」）
 *
 * 这一页承载系统的技术核心 —— 三康螺旋引擎（SK-Engine）。设计目标：
 *
 *   1. **把「三个指数会互相拖累」讲成可核对的机制**，而不是一个分数：
 *      分指数 + 权重 → 综合指数（加权几何平均）→ 螺旋增益 λ → 六条传导通道 → 干预杠杆排序。
 *   2. **数字全部来自接口**。分值与等级取 `/api/sk/score`，λ/权重/通道取 `/api/sk/engine`，
 *      杠杆排序取 `/api/sk/leverage`，推演取 `/api/sk/simulate`；
 *      页面里没有一处写死的分值、权重或增益。权重按接口下发的指数显示，
 *      因此界面上不会出现「文案说 0.40、接口其实给了别的值」这类口径漂移。
 *   3. **如实标注边界**。接口返回 `calibrated: false`，页面就显示「未校准」并说明含义；
 *      拿不到的字段显示「缺失」，绝不用 0 冒充；推演明确标注为情景推演、不是预测。
 *
 * 可验证逻辑全部在 `skModel.ts`（纯函数，Node 可直接跑 `skModel.test.ts`）；
 * 本文件只负责取数与渲染。
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { navigationCaliber, skCaliber } from '@hew3/shared';
import { api } from '../../lib/api.ts';
import { charts } from '../../lib/charts.ts';
import { Badge, Button, Card, Chart, ListItem, StateBox, Table } from '../../components/ui.tsx';
import {
  MISSING,
  toChannels,
  toCompositeView,
  toLeverageRows,
  toSimulationView,
  toSpiralView,
  type AxisNameTable,
  type SkEngineResponse,
  type SkLeverageResponse,
  type SkScoreResponse,
  type SkSimulateResponse,
} from './skModel.ts';

/** 轴名表：口径来自 `@hew3/shared` 的 skCaliber（文案常量，非业务数字）。 */
const AXIS_NAMES: AxisNameTable = {
  H: skCaliber.axes[0].name,
  E: skCaliber.axes[1].name,
  // 小程序受审核限制使用「资产健康」；H5 恢复品牌口径「财富健康」
  W: '财富健康',
};

/** 页面标题取导航口径，避免与底部导航各写一份。 */
const PAGE_TITLE = navigationCaliber.find((n) => n.id === 'sk')?.title ?? skCaliber.name;

/**
 * 推演情景：**只描述「若有这么一次冲击」，不预设任何成员数据**。
 * 冲击幅度是请求参数（服务端 zod 校验 0–40 的一次性冲击），不是业务展示数字；
 * 起算分值、个体化基准、干预强度都由服务端按成员存档值取默认。
 */
const SHOCK_SCENARIOS = [
  { key: 'none', label: '不施加冲击', note: '只观察当前偏离在完全不干预下会怎么走' },
  { key: 'health', label: '健康事件冲击', note: '对 H 轴施加一次性下行冲击' },
  { key: 'operation', label: '经营波动冲击', note: '对 E 轴施加一次性下行冲击' },
  { key: 'wealth', label: '财富波动冲击', note: '对 W 轴施加一次性下行冲击' },
] as const;

type ShockKey = (typeof SHOCK_SCENARIOS)[number]['key'];

function shockOf(key: ShockKey): number[] {
  if (key === 'health') return [12, 0, 0];
  if (key === 'operation') return [0, 12, 0];
  if (key === 'wealth') return [0, 0, 12];
  return [0, 0, 0];
}

/** 分值等级 → 徽章色调（等级字符串来自接口，这里只决定颜色）。 */
function gradeTone(grade: string | undefined, axis?: 'H' | 'E' | 'W'): 'h' | 'e' | 'w' | 'ok' | 'warn' | 'danger' | 'ghost' {
  if (!grade) return 'ghost';
  if (axis) return axis === 'H' ? 'h' : axis === 'E' ? 'e' : 'w';
  if (grade.startsWith('A')) return 'ok';
  if (grade.startsWith('B')) return 'warn';
  return 'danger';
}

export function SkPage() {
  const navigate = useNavigate();
  const [shockKey, setShockKey] = useState<ShockKey>('none');

  const engineQuery = useQuery({
    queryKey: ['sk', 'engine'],
    queryFn: () => api.get<SkEngineResponse>('/api/sk/engine'),
  });
  const scoreQuery = useQuery({
    queryKey: ['sk', 'score'],
    queryFn: () => api.post<SkScoreResponse>('/api/sk/score', {}),
  });
  const leverageQuery = useQuery({
    queryKey: ['sk', 'leverage'],
    queryFn: () => api.post<SkLeverageResponse>('/api/sk/leverage', {}),
  });
  // 推演是交互式计算：服务端默认取成员存档分值，并按「不干预」给出最需要看见的那条轨迹
  const simulateQuery = useQuery({
    queryKey: ['sk', 'simulate', shockKey],
    queryFn: () =>
      api.post<SkSimulateResponse>('/api/sk/simulate', {
        shock: shockOf(shockKey),
        withIntervention: false,
      }),
  });

  const header = (
    <div className="scr-head">
      <button type="button" className="back" onClick={() => navigate('/ai')} aria-label="返回智能体「叁」">
        <svg viewBox="0 0 24 24">
          <path d="M15 5l-7 7 7 7" />
        </svg>
      </button>
      <div className="scr-title">
        {PAGE_TITLE}
        <small>SK-ENGINE · 三康螺旋引擎</small>
      </div>
    </div>
  );

  if (engineQuery.isLoading || scoreQuery.isLoading) {
    return (
      <div className="page">
        {header}
        <div className="skeleton skel-card" style={{ height: 168 }} />
        <div className="skeleton skel-card" style={{ height: 140 }} />
        <div className="skeleton skel-card" />
      </div>
    );
  }

  if (engineQuery.isError || scoreQuery.isError || !engineQuery.data || !scoreQuery.data) {
    return (
      <div className="page">
        {header}
        <StateBox
          kind="error"
          title="三康看板数据加载失败"
          desc="无法取回三康评分或引擎参数。为避免展示一个来路不明的分数，这里不做估算。"
          action={
            <Button
              small
              onClick={() => {
                void engineQuery.refetch();
                void scoreQuery.refetch();
                void leverageQuery.refetch();
                void simulateQuery.refetch();
              }}
            >
              重新加载
            </Button>
          }
        />
      </div>
    );
  }

  const composite = toCompositeView(scoreQuery.data, AXIS_NAMES);
  const spiral = toSpiralView(engineQuery.data);
  const leverage = toLeverageRows(leverageQuery.data);
  const channels = toChannels(engineQuery.data, AXIS_NAMES);
  const simulation = simulateQuery.data ? toSimulationView(simulateQuery.data, AXIS_NAMES) : null;

  const axisToneClass = (key: 'H' | 'E' | 'W'): string => (key === 'H' ? 'hl-h' : key === 'E' ? 'hl-e' : 'hl-w');

  return (
    <div className="page">
      {header}

      {/* ---------------- 一、三康综合指数 ---------------- */}
      <Card
        title={skCaliber.name}
        more={<span className="data-tag">加权几何平均</span>}
      >
        <div className="row between" style={{ alignItems: 'flex-end' }}>
          <div>
            <div className="row-flex" style={{ alignItems: 'baseline', gap: 8 }}>
              <span className="num" style={{ fontSize: 44, fontWeight: 700, lineHeight: 1 }}>
                {composite.compositeDisplay}
              </span>
              <Badge tone={gradeTone(composite.grade)}>
                {composite.grade ?? MISSING}
                {composite.gradeText ? ` ${composite.gradeText}` : ''}
              </Badge>
            </div>
            <div className="text-tiny text-muted" style={{ marginTop: 8 }}>
              {composite.measuredAt
                ? `数据来源于 ${composite.measuredAt.slice(0, 10)} 的三康档案`
                : '接口未标注数据时间'}
              {composite.source === 'member_record' ? ' · 分值取成员存档值' : composite.source === 'client_override' ? ' · 分值由本次参数指定' : ''}
            </div>
          </div>
        </div>

        <div className="divider" />

        <div className="sec-note">
          它是 H / E / W 的<b>加权几何平均</b>：权重即几何平均的指数，由引擎接口下发
          （本页显示为 {composite.axes.map((a) => `${a.key} ${a.weightDisplay}`).join(' · ')}）。
          用几何平均而不是算术平均，是因为算术平均下「一维塌陷」会被另外两维掩盖，
          而几何平均会让塌陷直接反映到总分 —— 这就是「健康是 1，其余是后面的 0」的数学表达。
        </div>
        {composite.formula ? (
          <div className="note-src">
            <b>合成公式</b>：{composite.formula}
            {composite.note ? <div style={{ marginTop: 6 }}>{composite.note}</div> : null}
          </div>
        ) : null}
        {!composite.available && (
          <div className="note-src" style={{ borderLeftColor: 'var(--warn)' }}>
            接口未返回完整字段（{composite.missing.join('、')}），相关位置如实显示「{MISSING}」，未做任何估算。
          </div>
        )}
      </Card>

      {/* ---------------- 二、三个分指数 ---------------- */}
      <Card title="三个分指数">
        <div className="grid3">
          {composite.axes.map((axis) => (
            <div key={axis.key ?? 'unknown'}>
              <div className="text-tiny text-muted">
                {axis.key ? `${axis.key} ` : ''}
                {axis.name}
              </div>
              <div className={`num ${axis.key ? axisToneClass(axis.key) : ''}`} style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>
                {axis.display}
              </div>
              <div style={{ marginTop: 6 }}>
                <Badge tone={gradeTone(axis.grade, axis.key)}>
                  {axis.grade ?? MISSING}
                  {axis.gradeText ? ` ${axis.gradeText}` : ''}
                </Badge>
              </div>
              <div className="text-tiny text-muted" style={{ marginTop: 6 }}>
                几何平均权重 {axis.weightDisplay}
              </div>
            </div>
          ))}
        </div>
        <div className="divider" />
        <div className="text-tiny text-muted" style={{ lineHeight: 1.75 }}>
          {skCaliber.axes.map((a) => `${a.key} ${a.name}：${a.sub}`).join('　|　')}
        </div>
      </Card>

      {/* ---------------- 三、螺旋状态 ---------------- */}
      <Card
        title="螺旋状态"
        more={<Badge tone={spiral.calibratedTone}>{spiral.calibratedText}</Badge>}
      >
        <div className="row between" style={{ alignItems: 'flex-end' }}>
          <div>
            <div className="text-tiny text-muted">螺旋增益 λ = ρ(G)（传导矩阵谱半径）</div>
            <div className="row-flex" style={{ alignItems: 'baseline', gap: 8, marginTop: 4 }}>
              <span className="num" style={{ fontSize: 32, fontWeight: 700, lineHeight: 1 }}>
                {spiral.lambdaDisplay}
              </span>
              <Badge tone={spiral.tone === 'danger' ? 'danger' : spiral.tone === 'warn' ? 'warn' : spiral.tone === 'ok' ? 'ok' : 'ghost'}>
                {spiral.label}
              </Badge>
            </div>
          </div>
        </div>

        <div className="sec-note" style={{ marginTop: 10 }}>
          λ &gt; 1 表示偏离会被放大：任一维度的下滑都会沿传导通道传到另外两维、再反传回来，
          因此<b>不干预不会自愈</b>，只会缓慢累积。这也是「主动守护」而不是「被动响应」的数学依据。
        </div>
        {spiral.desc ? <div className="text-tiny text-muted" style={{ marginTop: 8, lineHeight: 1.75 }}>{spiral.desc}</div> : null}
        {spiral.amplifying !== undefined && (
          <div className="text-tiny text-muted" style={{ marginTop: 6 }}>
            当前判定：λ {spiral.lambdaDisplay} {spiral.amplifying ? '＞' : '＜'} 1 ·{' '}
            {spiral.amplifying ? '处于放大区，需要外部干预' : '处于收敛区，冲击会自然衰减'}
          </div>
        )}

        {/* 诚实标注：接口返回 calibrated:false 时，这里必须照实说，不得美化成「已校准」 */}
        <div className="note-src" style={{ marginTop: 10 }}>
          <b>标定状态：{spiral.calibratedText}</b>
          {spiral.calibrated === false ? (
            <div style={{ marginTop: 6 }}>
              传导矩阵的弹性系数为专家先验值，尚未用真实队列数据回归标定；未标定前，本页的 λ、
              推演与杠杆排序用于<b>相对比较与干预排序</b>，不作为绝对预测。
            </div>
          ) : null}
          {spiral.notice ? <div style={{ marginTop: 6 }}>{spiral.notice}</div> : null}
          {spiral.missing.length > 0 ? (
            <div style={{ marginTop: 6 }}>引擎接口未返回：{spiral.missing.join('、')}（如实显示「{MISSING}」）。</div>
          ) : null}
        </div>
      </Card>

      {/* ---------------- 四、杠杆排序 ---------------- */}
      <Card
        title="干预杠杆排序"
        more={<span className="card-more">同等强度下，投在哪个轴回报最高</span>}
      >
        {leverageQuery.isLoading ? (
          <div className="skeleton skel-line" />
        ) : leverageQuery.isError ? (
          <StateBox
            kind="error"
            title="杠杆排序加载失败"
            desc="无法取回各轴的边际回报，因此这里不给出任何优先顺序。"
            action={
              <Button small onClick={() => void leverageQuery.refetch()}>
                重新加载
              </Button>
            }
          />
        ) : leverage.rows.length === 0 ? (
          <StateBox title="暂无杠杆排序结果" desc="接口未返回任何维度的边际回报，可能尚未建立完整的三康档案。" />
        ) : (
          <>
            <Chart
              html={charts.hbars({
                items: leverage.rows.map((row) => ({
                  label: `${row.key ?? MISSING} ${row.name}`,
                  value: row.gain ?? 0,
                  max: 100,
                  color: row.tone,
                  text: row.gainDisplay,
                })),
              })}
            />
            <div className="sp12" />
            <Table
              head={['顺序', '轴', '综合指数增益', '本轴改善', '几何平均权重']}
              rows={leverage.rows.map((row) => [
                `第 ${row.order}`,
                <span key={`${row.key}-n`} className={row.key ? axisToneClass(row.key) : ''}>
                  {row.key ? `${row.key} ` : ''}
                  {row.name}
                </span>,
                <b key={`${row.key}-g`}>{row.gainDisplay}</b>,
                row.absorptionDisplay,
                composite.axes.find((a) => a.key === row.key)?.weightDisplay ?? MISSING,
              ])}
            />
            <div className="sec-note" style={{ marginTop: 10 }}>
              {leverage.conclusion || '接口未给出结论文案，请以上表数值为准。'}
            </div>
            {leverage.reordered && (
              <div className="note-src" style={{ borderLeftColor: 'var(--warn)' }}>
                注意：接口返回的顺序不是按增益降序，本页已按增益重新排序（原顺序未被采用），
                以免「先看到的行看起来更优」造成误读。
              </div>
            )}
            {leverage.calibrated === false && (
              <div className="text-tiny text-muted" style={{ marginTop: 6 }}>
                该排序同样基于未标定的弹性系数，适合用于比较三个轴之间的相对优先级。
              </div>
            )}
          </>
        )}
      </Card>

      {/* ---------------- 五、传导通道 ---------------- */}
      <Card
        title="传导通道"
        more={<span className="card-more">{channels.length > 0 ? `${channels.length} 条` : MISSING}</span>}
      >
        {channels.length === 0 ? (
          <StateBox title="引擎未返回传导通道" desc="传导矩阵的可读通道缺失，此时无法解释跨维度影响。" />
        ) : (
          <>
            <div className="text-tiny text-muted" style={{ lineHeight: 1.75, marginBottom: 8 }}>
              这六条通道就是「为什么这三个指数会互相拖累」的答案：每条都带弹性系数（强度）、
              时滞（多久之后显现）与依据。算法只在服务端，这里呈现的是引擎自述。
            </div>
            {channels.map((ch) => (
              <ListItem
                key={`${ch.from}-${ch.to}-${ch.lag}`}
                icon={<span className={ch.to === 'H' ? 'hl-h' : ch.to === 'E' ? 'hl-e' : 'hl-w'}>{ch.from}→{ch.to}</span>}
                title={ch.name}
                sub={
                  <>
                    {ch.fromName} → {ch.toName} · 强度 g {ch.gDisplay} · 时滞 {ch.lag}
                    <br />
                    依据：{ch.path}
                  </>
                }
                right={<span className="num text-tiny">{ch.gDisplay}</span>}
              />
            ))}
          </>
        )}
      </Card>

      {/* ---------------- 六、推演入口 ---------------- */}
      <Card
        title="若不干预：情景推演"
        more={<Badge tone="warn">情景推演 · 非预测</Badge>}
      >
        <div className="text-tiny text-muted" style={{ lineHeight: 1.75 }}>
          选择一种情景，看这条冲击在「完全不干预」时会走到哪里。起算分值与个体化基准取您的三康档案，
          由服务端计算；本页只提交情景参数。
        </div>

        <div className="chip-row" style={{ marginTop: 10 }}>
          {SHOCK_SCENARIOS.map((s) => (
            <button
              key={s.key}
              type="button"
              className="chip"
              style={shockKey === s.key ? { borderColor: 'var(--h)', color: 'var(--h)' } : undefined}
              onClick={() => setShockKey(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="text-tiny text-muted" style={{ marginTop: 8 }}>
          {SHOCK_SCENARIOS.find((s) => s.key === shockKey)?.note ?? ''}
        </div>

        {simulateQuery.isLoading ? (
          <div className="skeleton skel-line" style={{ marginTop: 12 }} />
        ) : simulateQuery.isError ? (
          <StateBox
            kind="error"
            title="推演失败"
            desc="无法取回推演结果。情景推演依赖引擎参数，这里不做本地估算。"
            action={
              <Button small onClick={() => void simulateQuery.refetch()}>
                重新推演
              </Button>
            }
          />
        ) : !simulation || !simulation.available ? (
          <StateBox
            title="推演结果不完整"
            desc={simulation?.reason || '接口未返回完整的推演轨迹，缺失项如实显示「缺失」。'}
            action={
              <Button small onClick={() => void simulateQuery.refetch()}>
                重新推演
              </Button>
            }
          />
        ) : (
          <>
            <div className="divider" />
            <div className="grid3">
              <div>
                <div className="text-tiny text-muted">不干预 · 窗口末月综合指数</div>
                <div className="num" style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                  {simulation.withoutCompositeDisplay}
                </div>
              </div>
              <div>
                <div className="text-tiny text-muted">默认干预强度 · 窗口末月综合指数</div>
                <div className="num hl-h" style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                  {simulation.withCompositeDisplay}
                </div>
              </div>
              <div>
                <div className="text-tiny text-muted">推演窗口</div>
                <div className="num" style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                  {simulation.months === undefined ? MISSING : `${simulation.months} 个月`}
                </div>
              </div>
            </div>

            <div className="sec-note" style={{ marginTop: 10 }}>
              {simulation.recovered
                ? `在 ${simulation.months ?? MISSING} 个月窗口内，三维已于第 ${simulation.recovery} 个月同时回到个体化基准。`
                : simulation.recovery === -1
                  ? `在 ${simulation.months ?? MISSING} 个月窗口内三维均未回到个体化基准（recovery = -1），偏离仍在累积。`
                  : '接口未给出回稳月份，无法判断是否回到基准。'}
            </div>

            {/* 两个情景给出同一结果时如实说明：不制造「干预有效」的观感 */}
            {simulation.withComposite !== undefined &&
              simulation.withoutComposite !== undefined &&
              Math.abs(simulation.withComposite - simulation.withoutComposite) < 0.05 && (
                <div className="note-src" style={{ borderLeftColor: 'var(--warn)' }}>
                  本情景下两条轨迹的窗口末月综合指数相同（{simulation.withoutCompositeDisplay}）：
                  在当前起算分值与个体化基准下，默认干预没有产生可测量的额外收益，
                  因此<b>不能据此声称干预有效</b>；同时这也说明「不干预会自愈」不成立 ——
                  三维在窗口内均未回到基准，与 λ &gt; 1 的判定一致。
                </div>
              )}

            <Table
              head={['轴', '窗口末月分值', '窗口内最低点', '是否回到基准']}
              rows={simulation.axes.map((axis) => [
                <span key={`${axis.key}-t`} className={axisToneClass(axis.key)}>
                  {axis.key} {axis.name}
                </span>,
                <b key={`${axis.key}-f`}>{axis.finalDisplay}</b>,
                axis.minimum === undefined
                  ? MISSING
                  : `${axis.minimum.toFixed(1)}${axis.minimumMonth === undefined ? '' : `（第 ${axis.minimumMonth} 月）`}`,
                axis.recovered === undefined ? MISSING : axis.recovered ? '是' : '否',
              ])}
            />

            {simulation.axes[0].trajectory.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div className="text-tiny text-muted">
                  不干预轨迹（H 轴，第 0 个月 → 第 {simulation.months ?? MISSING} 个月）
                </div>
                <Chart
                  html={charts.spark({
                    data: simulation.axes[0].trajectory,
                    w: 600,
                    h: 64,
                    color: 'h',
                    fill: true,
                  })}
                />
              </div>
            )}

            <div className="note-src" style={{ marginTop: 10 }}>
              <b>{simulation.disclaimer}</b>
              <div style={{ marginTop: 6 }}>
                基准来源：{simulation.baselineSource === 'expert_prior'
                  ? '专家先验值（尚未用真实队列数据标定）'
                  : simulation.baselineSource === 'client_override'
                    ? '本次推演传入的自定义基准'
                    : simulation.baselineSource || MISSING}
                。标定状态：{simulation.calibratedText}。
              </div>
              {simulation.notice ? <div style={{ marginTop: 6 }}>{simulation.notice}</div> : null}
            </div>
          </>
        )}
      </Card>

      {/* ---------------- 七、其余页面的入口（三康页是新的信息架构中枢之一） ---------------- */}
      <Card title="继续查看" more={<span className="card-more">原有页面全部保留</span>}>
        <ListItem
          icon={<span className="hl-h">叁</span>}
          title="智能体「叁」"
          sub="把三条线索放在一起问：现金流压力如何传到睡眠与恢复能力"
          right={<span className="text-tiny text-muted">›</span>}
          onClick={() => navigate('/ai')}
        />
        <ListItem
          icon={<span className="hl-h">H</span>}
          title="生命健康"
          sub={skCaliber.axes[0].sub}
          right={<span className="text-tiny text-muted">›</span>}
          onClick={() => navigate('/health')}
        />
        <ListItem
          icon={<span className="hl-e">E</span>}
          title="企业治理健康"
          sub={skCaliber.axes[1].sub}
          right={<span className="text-tiny text-muted">›</span>}
          onClick={() => navigate('/enterprise')}
        />
        <ListItem
          icon={<span className="hl-w">W</span>}
          title="财富健康"
          sub="6 类财富参数"
          right={<span className="text-tiny text-muted">›</span>}
          onClick={() => navigate('/wealth')}
        />
        <ListItem
          icon={<span>◍</span>}
          title="三螺旋总览（原首页）"
          sub="健康—企业—财富作为一个整体的总览视图"
          right={<span className="text-tiny text-muted">›</span>}
          onClick={() => navigate('/')}
        />
        <ListItem
          icon={<span>◍</span>}
          title="传承学院"
          sub="三层五维培养矩阵"
          right={<span className="text-tiny text-muted">›</span>}
          onClick={() => navigate('/succession')}
        />
        <div className="text-tiny text-muted" style={{ marginTop: 8 }}>
          引擎口径：{skCaliber.scale.items} 项指标 / {skCaliber.scale.dims} 维度（
          {skCaliber.scale.breakdown.map((b) => `${b.name} ${b.n}`).join(' · ')}）。
          <Link to="/health/reports" style={{ marginLeft: 6 }}>
            查看检测报告 ›
          </Link>
        </div>
      </Card>
    </div>
  );
}
